<template>
  <MainLayout>
    <ContentPageHeader functionName="店舗移動管理" pageName="店舗移動一覧">
    </ContentPageHeader>
    
    <!-- 基本検索セクション -->
    <div class="search-section" @keyup.enter="search()">
      <div class="search-form">
        <div class="date-range-container">
          <div class="date-field">
            <FormDateField 
              label="出荷登録日（From）" 
              id="shipmentRegistrationDateFrom" 
              v-model="searchParams.shipmentRegistrationDateFrom"
            />
          </div>
          <span class="date-range-separator">～</span>
          <div class="date-field">
            <FormDateField 
              label="出荷登録日（To）" 
              id="shipmentRegistrationDateTo" 
              v-model="searchParams.shipmentRegistrationDateTo"
            />
          </div>
        </div>
        
        <FormSelectSearchField 
          label="移動元店舗" 
          id="movingSourceStore" 
          v-model="searchParams.movingSourceStore"
          :options="storeOptions"
          placeholder="選択してください"
          size="small"
        />
        
        <FormSelectSearchField 
          label="移動先店舗" 
          id="destinationStore" 
          v-model="searchParams.destinationStore"
          :options="storeOptions"
          placeholder="選択してください"
          size="small"
        />
      </div>
    </div>
    
    <!-- 検索ボタンエリア -->
    <div class="search-buttons-section">
      <div class="search-buttons">
        <button 
          v-if="showCSVButton" 
          class="btn btn-publish-registration" 
          @click="exportCSV"
        >
          CSV出力
        </button>
        <button class="btn btn-secondary" @click="resetSearch">リセット</button>
        <button class="btn btn-primary" @click="storeTransferSearch">検索</button>
      </div>
    </div>
    
    <!-- 検索結果テーブル -->
    <div class="search-result">
      <div class="search-result-header">
        <h4>検索結果</h4>
      </div>
      <FormListTable
        :items="sortedStoreTransfers"
        :columns="columns"
        :actions="actions"
        :perPage="perPage"
        :page="currentPage"
        :totalItems="totalCount"
        :actionColumnWidth="260"
        :emptyMessage="'店舗移動データがありません'"
        @page-change="onPageChange"
        @action="onAction"
      >
        <!-- 選択用チェックボックスの表示 -->
        <template #select="{ item }">
          <input 
            type="checkbox" 
            :checked="selectedStoreTransfers.includes(item.id)" 
            @change="toggleSelectStoreTransfer(item.id)" 
          />
        </template>
        
        <!-- ヘッダーチェックボックス -->
        <template #header-select>
          <input 
            type="checkbox" 
            :checked="isAllSelected" 
            @change="toggleSelectAll" 
          />
        </template>
        
        <!-- カスタムヘッダーテンプレート -->
        <template v-for="col in columns.filter(col => col.sortable)" :key="`header-${col.key}`" #[`header-${col.key}`]>
          <div class="column-header" @click="sortBy(col.key)">
            {{ col.label }}
            <span class="sort-icon">
              <i v-if="sortKey === col.key && sortOrder === 'asc'" class="fa fa-sort-up" aria-hidden="true"></i>
              <i v-else-if="sortKey === col.key && sortOrder === 'desc'" class="fa fa-sort-down" aria-hidden="true"></i>
              <i v-else class="fa fa-sort" aria-hidden="true"></i>
            </span>
          </div>
        </template>
        
        <!-- 完了ステータス表示 -->
        <template #isComplete="{ item }">
          <span 
            v-if="item.isArrivalComplete === COMPLETE && item.isShippingComplete === COMPLETE" 
            class="status-complete"
          >
            ○
          </span>
          <span v-else class="status-incomplete"></span>
        </template>
      </FormListTable>
    </div>
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/layout/MainLayout.vue'
import ContentPageHeader from '@/components/layout/ContentPageHeader.vue'
import FormSelectSearchField from '@/components/form/FormSelectSearchField.vue'
import FormDateField from '@/components/form/FormDateField.vue'
import FormListTable from '@/components/form/FormListTable.vue'
import service from '../../services/service';
import constants from '../../constants/constants.json';
import { showLoading, hideLoading } from '@/utils/loadingEventBus';

export default {
  name: 'StoreTransferListView',
  components: {
    MainLayout,
    ContentPageHeader,
    FormSelectSearchField,
    FormDateField,
    FormListTable
  },
  data() {
    return {
      // 検索パラメータ
      searchParams: {
        shipmentRegistrationDateFrom: '',
        shipmentRegistrationDateTo: '',
        movingSourceStore: '',
        destinationStore: ''
      },
      
      // ページング
      currentPage: 0,
      perPage: 50,
      totalCount: 0,
      
      // 店舗移動データ
      storeTransfers: [],
      
      // 選択された店舗移動ID配列
      selectedStoreTransfers: [],
      
      // 店舗オプション
      storeOptions: [],
      
      // ソート
      sortKey: '',
      sortOrder: 'asc',
      
      // テーブルカラム定義
      columns: [
        { key: 'select', label: '', minWidth: 40, maxWidth: 40 },
        { key: 'id', label: 'ID', sortable: true, minWidth: 80, maxWidth: 100 },
        { key: 'shipmentRegistrationDate', label: '出荷登録日', sortable: true, minWidth: 130, maxWidth: 150 },
        { key: 'itemCount', label: '商品点数', sortable: true, minWidth: 100, maxWidth: 120 },
        { key: 'movingSourceStoreName', label: '移動元店舗', sortable: true, minWidth: 150, maxWidth: 180 },
        { key: 'destinationStoreName', label: '移動先店舗', sortable: true, minWidth: 150, maxWidth: 180 },
        { key: 'isComplete', label: '完了', sortable: true, minWidth: 80, maxWidth: 100 },
        { key: 'purchaseTotalAmount', label: '買取合計額', sortable: true, minWidth: 120, maxWidth: 150 }
      ],
      
      // アクション定義
      actions: [
        { 
          label: '出荷編集', 
          buttonClass: 'btn-secondary', 
          action: this.goToShipping,
          disabled: item => (
            item.isShippingComplete === constants.COMPLETE || 
            (this.$store.getters['auth/currentUser']?.authority != constants.Authority.ADMIN.id && 
              !this.isCurrentUserStore(item.movingSourceStore)
            )
          )
        },
        { 
          label: '入荷処理', 
          buttonClass: 'btn-primary', 
          action: this.processArrival,
          disabled: item => (
            item.isArrivalComplete === constants.COMPLETE || 
            (this.$store.getters['auth/currentUser']?.authority != constants.Authority.ADMIN.id && 
              !this.isCurrentUserStore(item.destinationStore)
            )
          )
        },
        { 
          label: '', 
          buttonClass: 'btn-secondary',
          icon: 'fa-file-alt',
          action: this.viewDetail,
          disabled: item => (
            item.isArrivalComplete !== constants.COMPLETE
          )
        },
      ]
    }
  },
  computed: {
    // ソート済み店舗移動データ
    sortedStoreTransfers() {
      if (!this.sortKey) {
        return this.storeTransfers;
      }

      return [...this.storeTransfers].sort((a, b) => {
        let aValue = a[this.sortKey];
        let bValue = b[this.sortKey];

        // 金額カラムの場合は通貨記号を削除して数値として比較
        if (this.sortKey === 'purchaseTotalAmount') {
          const parseMonetaryValue = (value) => {
            return value
              ? parseInt(String(value).replace(/[¥,]/g, ''))
              : 0;
          };

          const aNum = parseMonetaryValue(aValue);
          const bNum = parseMonetaryValue(bValue);
          return this.sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
        }

        // 数値の場合は単純に比較
        if (typeof aValue !== 'string' || typeof bValue !== 'string') {
          return this.sortOrder === 'asc'
            ? (aValue > bValue ? 1 : -1)
            : (aValue < bValue ? 1 : -1);
        }

        // 文字列の場合は大文字小文字を区別せずに比較（最後に実行）
        return this.sortOrder === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      });
    },
    
    // 全選択状態の計算
    isAllSelected() {
      return 0 < this.sortedStoreTransfers.length && this.selectedStoreTransfers.length === this.sortedStoreTransfers.length;
    },
    
    // CSV出力ボタン表示条件
    showCSVButton() {
      return 0 < this.selectedStoreTransfers.length;
    },

    // 完了条件
    COMPLETE() {
      return constants.COMPLETE;
    }
  },
  methods: {
    // 検索条件を保存
    saveSearchConditions() {
      const conditions = {
        searchParams: { ...this.searchParams },
        currentPage: this.currentPage,
        perPage: this.perPage,
        sortKey: this.sortKey,
        sortOrder: this.sortOrder
      };
      localStorage.setItem('storeTransferSearchConditions', JSON.stringify(conditions));
    },
    
    // 検索条件を復元
    loadSearchConditions() {
      const saved = localStorage.getItem('storeTransferSearchConditions');
      if (saved) {
        const conditions = JSON.parse(saved);
        this.searchParams = { ...conditions.searchParams };
        this.currentPage = conditions.currentPage || 0;
        this.perPage = conditions.perPage || 50;
        this.sortKey = conditions.sortKey || '';
        this.sortOrder = conditions.sortOrder || 'asc';
      }
    },
    
    // 検索実行
    async search(page) {
      try {
        // 選択されている情報の解除
        this.selectedStoreTransfers = [];
        showLoading();

        // ページングの更新
        if (page !== undefined) {
          this.currentPage = page;
        } else {
          this.currentPage = 0;
        }

        const params = {
          ...this.searchParams,
          page: this.currentPage,
          perPage: this.perPage
        };
        
        // ソート情報追加
        if (this.sortKey) {
          params.sortBy = this.sortKey;
          params.sortDirection = this.sortOrder;
        }
        
        const response = await service.searchStoreTransfers(params);
        if (response && response.status === 200) {
          this.storeTransfers = response.data.content;
          this.totalCount = response.data.totalElements;
        } else {
          console.error('店舗移動データの取得に失敗しました');
        }
      } catch (error) {
        console.error('店舗移動データの検索エラー:', error);
        alert('店舗移動データの検索中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
      
      // 検索条件を保存
      this.saveSearchConditions();
    },
    // 検索ボタン押下処理
    storeTransferSearch() {
      this.search();
    },
    // 検索条件リセット
    resetSearch() {
      this.searchParams = {
        shipmentRegistrationDateFrom: '',
        shipmentRegistrationDateTo: '',
        movingSourceStore: '',
        destinationStore: ''
      };
      this.currentPage = 0;
      this.search();
    },
    
    // ページ変更
    onPageChange(page) {
      this.search(page - 1);
    },
    
    // ソート
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortOrder = 'asc';
      }
      this.search();
    },
    
    // アクション処理
    onAction({ action, item }) {
      if (action === '出荷編集') {
        this.goToShipping(item);
      } else if (action === '入荷処理') {
        this.processArrival(item);
      } else if (action === '詳細') {
        this.viewDetail(item);
      }
    },
    
    // 出荷編集
    goToShipping(item) {
      sessionStorage.setItem('legitimateNavigation', 'StoreTransferShipping');
      this.$router.push({
        name: 'StoreTransferShipping',
        query: { id: item.id }
      });
    },
    
    // 入荷処理
    processArrival(item) {
      sessionStorage.setItem('legitimateNavigation', 'StoreTransferArrival');
      this.$router.push({ 
        name: 'StoreTransferArrival',
        query: { id: item.id } 
      });
    },
    
    // 詳細表示
    viewDetail(item) {
      sessionStorage.setItem('legitimateNavigation', 'StoreTransferArrival');
      this.$router.push({ 
        name: 'StoreTransferArrival',
        query: { id: item.id, mode: 'detail' } 
      });
    },
    
    // 店舗情報取得
    async loadStoreOptions() {
      try {
        const response = await service.getStoreList();
        this.storeOptions = response.data.stores.map(store => ({
          value: store.id,
          label: store.storeName
        }));
      } catch (error) {
        console.error('店舗情報の取得エラー:', error);
        alert('店舗情報の取得中にエラーが発生しました。');
      }
    },
    
    // ログインユーザーの店舗かどうか判定
    isCurrentUserStore(storeId) {
      const userStoreId = this.$store.getters['auth/currentUser']?.storeId;
      return userStoreId === storeId;
    },
    
    // 店舗移動の選択切り替え
    toggleSelectStoreTransfer(id) {
      const index = this.selectedStoreTransfers.indexOf(id);
      if (index === -1) {
        this.selectedStoreTransfers.push(id);
      } else {
        this.selectedStoreTransfers.splice(index, 1);
      }
    },
    
    // 全選択/全解除切り替え
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedStoreTransfers = [];
      } else {
        this.selectedStoreTransfers = this.sortedStoreTransfers.map(item => item.id);
      }
    },
    
    // CSV出力処理
    async exportCSV() {
      if (this.selectedStoreTransfers.length === 0) {
        return;
      }
      
      // 確認ダイアログ表示
      const isConfirmed = confirm('選択した店舗移動情報をダウンロードしますか？');
      if (!isConfirmed) {
        return;
      }
      
      try {
        showLoading();
        // APIを呼び出してCSVをダウンロード
        const response = await service.exportStoreTransferCSV(this.selectedStoreTransfers);

        // 1. レスポンスデータ（CSVテキスト）を取得
        const csvText = response.data;

        // 2. Blob作成（Shift_JISで返ってくる想定なのでtypeはtext/csv; charset=Shift_JIS）
        const blob = new Blob([csvText], { type: 'text/csv' });

        // 3. ファイル名生成
        const now = new Date();
        const pad = n => n.toString().padStart(2, '0');
        const filename =
          `店舗移動_${now.getFullYear()}`
          + `${pad(now.getMonth() + 1)}`
          + `${pad(now.getDate())}`
          + `${pad(now.getHours())}`
          + `${pad(now.getMinutes())}`
          + `${pad(now.getSeconds())}.csv`;

        // 4. ダウンロード処理
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('CSV出力エラー:', error);
        alert('CSV出力中にエラーが発生しました');
      } finally {
        hideLoading();
      }
    }
  },
  async mounted() {
    // 検索条件を復元
    this.loadSearchConditions();
    
    this.loadStoreOptions();
    this.search();
  }
}
</script>

<style scoped>
</style>
